import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import {
  GalleryCart,
  GalleryCartItem,
  GalleryPackageItem,
  GalleryCartContextType,
} from '../types/gallery';
import { galleryApiService } from '../services/galleryApiService';
import { useGalleryConfig } from './GalleryConfigContext';
import { useCustomerAuth } from './CustomerAuthContext';

interface CartState {
  cart: GalleryCart;
  loading: boolean;
  error: string | null;
}

type CartAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_CART'; payload: GalleryCart }
  | { type: 'ADD_ITEM'; payload: GalleryCartItem }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { itemId: string; quantity: number } }
  | { type: 'ADD_PACKAGE'; payload: GalleryPackageItem }
  | { type: 'REMOVE_PACKAGE'; payload: string }
  | { type: 'APPLY_COUPON'; payload: { code: string; discount: number } }
  | { type: 'REMOVE_COUPON' }
  | { type: 'CLEAR_CART' }
  | { type: 'UPDATE_TOTALS'; payload: { subtotal: number; tax: number; total: number } };

const initialCart: GalleryCart = {
  items: [],
  packages: [],
  subtotal: 0,
  tax: 0,
  total: 0,
  discount: 0,
  currency: 'USD',
};

const initialState: CartState = {
  cart: initialCart,
  loading: false,
  error: null,
};

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'SET_CART':
      return { ...state, cart: action.payload, loading: false, error: null };
    case 'ADD_ITEM': {
      const existingItem = state.cart.items.find(
        item =>
          item.photoId === action.payload.photoId && item.resolution === action.payload.resolution
      );

      let updatedItems;
      if (existingItem) {
        updatedItems = state.cart.items.map(item =>
          item.id === existingItem.id
            ? { ...item, quantity: item.quantity + action.payload.quantity }
            : item
        );
      } else {
        updatedItems = [...state.cart.items, action.payload];
      }

      return {
        ...state,
        cart: { ...state.cart, items: updatedItems },
      };
    }
    case 'REMOVE_ITEM':
      return {
        ...state,
        cart: {
          ...state.cart,
          items: state.cart.items.filter(item => item.id !== action.payload),
        },
      };
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        cart: {
          ...state.cart,
          items: state.cart.items
            .map(item =>
              item.id === action.payload.itemId
                ? { ...item, quantity: action.payload.quantity }
                : item
            )
            .filter(item => item.quantity > 0),
        },
      };
    case 'ADD_PACKAGE': {
      const existingPackage = state.cart.packages.find(
        pkg => pkg.packageId === action.payload.packageId && pkg.eventId === action.payload.eventId
      );

      let updatedPackages;
      if (existingPackage) {
        // Replace existing package
        updatedPackages = state.cart.packages.map(pkg =>
          pkg.id === existingPackage.id ? action.payload : pkg
        );
      } else {
        updatedPackages = [...state.cart.packages, action.payload];
      }

      return {
        ...state,
        cart: { ...state.cart, packages: updatedPackages },
      };
    }
    case 'REMOVE_PACKAGE':
      return {
        ...state,
        cart: {
          ...state.cart,
          packages: state.cart.packages.filter(pkg => pkg.id !== action.payload),
        },
      };
    case 'APPLY_COUPON':
      return {
        ...state,
        cart: {
          ...state.cart,
          couponCode: action.payload.code,
          discount: action.payload.discount,
        },
      };
    case 'REMOVE_COUPON':
      return {
        ...state,
        cart: {
          ...state.cart,
          couponCode: undefined,
          discount: 0,
        },
      };
    case 'CLEAR_CART':
      return {
        ...state,
        cart: initialCart,
      };
    case 'UPDATE_TOTALS':
      return {
        ...state,
        cart: {
          ...state.cart,
          subtotal: action.payload.subtotal,
          tax: action.payload.tax,
          total: action.payload.total,
        },
      };
    default:
      return state;
  }
}

const GalleryCartContext = createContext<GalleryCartContextType | undefined>(undefined);

interface GalleryCartProviderProps {
  children: ReactNode;
}

export const GalleryCartProvider: React.FC<GalleryCartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { config } = useGalleryConfig();
  const { customer } = useCustomerAuth();

  // Load cart from localStorage or API on mount
  useEffect(() => {
    const loadCart = async () => {
      if (!config?.photographerId) return;

      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        let cart: GalleryCart;

        if (customer) {
          // Load cart from API for authenticated users
          cart = await galleryApiService.getCustomerCart(config.photographerId, customer.id);
        } else {
          // Load cart from localStorage for guests
          const cartKey = `gallery_cart_${config.photographerId}`;
          const savedCart = localStorage.getItem(cartKey);
          cart = savedCart ? JSON.parse(savedCart) : initialCart;
        }

        dispatch({ type: 'SET_CART', payload: cart });
      } catch {
        // Fall back to empty cart on load failure
        dispatch({ type: 'SET_CART', payload: initialCart });
      }
    };

    loadCart();
  }, [config, customer]);

  // Save cart to localStorage and API when it changes
  useEffect(() => {
    const saveCart = async () => {
      if (!config?.photographerId) return;

      const cartKey = `gallery_cart_${config.photographerId}`;

      // Always save to localStorage for guest access
      localStorage.setItem(cartKey, JSON.stringify(state.cart));

      // Save to API if user is authenticated
      if (customer) {
        try {
          await galleryApiService.saveCustomerCart(config.photographerId, customer.id, state.cart);
        } catch {
          // Error handled via UI state
        }
      }
    };

    // Debounce save to avoid too frequent API calls
    const timeoutId = setTimeout(saveCart, 500);
    return () => clearTimeout(timeoutId);
  }, [state.cart, config, customer]);

  const addItem = (item: Omit<GalleryCartItem, 'id'>) => {
    const newItem: GalleryCartItem = {
      ...item,
      id: `${item.photoId}_${item.resolution}_${Date.now()}`,
    };
    dispatch({ type: 'ADD_ITEM', payload: newItem });
    calculateTotals();
  };

  const removeItem = (itemId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: itemId });
    calculateTotals();
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { itemId, quantity } });
    calculateTotals();
  };

  const addPackage = (packageItem: Omit<GalleryPackageItem, 'id'>) => {
    const newPackage: GalleryPackageItem = {
      ...packageItem,
      id: `${packageItem.packageId}_${packageItem.eventId}_${Date.now()}`,
    };
    dispatch({ type: 'ADD_PACKAGE', payload: newPackage });
    calculateTotals();
  };

  const removePackage = (packageId: string) => {
    dispatch({ type: 'REMOVE_PACKAGE', payload: packageId });
    calculateTotals();
  };

  const applyCoupon = async (couponCode: string) => {
    if (!config?.photographerId) {
      throw new Error('Gallery configuration not loaded');
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const couponData = await galleryApiService.validateCoupon(
        config.photographerId,
        couponCode,
        state.cart
      );

      dispatch({
        type: 'APPLY_COUPON',
        payload: { code: couponCode, discount: couponData.discount },
      });
      calculateTotals();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid coupon code';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const removeCoupon = () => {
    dispatch({ type: 'REMOVE_COUPON' });
    calculateTotals();
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const calculateTotals = () => {
    // Calculate subtotal from items
    const itemsSubtotal = state.cart.items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    // Calculate subtotal from packages
    const packagesSubtotal = state.cart.packages.reduce((sum, pkg) => sum + pkg.price, 0);

    const subtotal = itemsSubtotal + packagesSubtotal;
    const discountAmount = state.cart.discount;
    const taxRate = config?.settings.currency === 'USD' ? 0.08 : 0; // 8% tax for USD
    const taxableAmount = Math.max(0, subtotal - discountAmount);
    const tax = taxableAmount * taxRate;
    const total = taxableAmount + tax;

    dispatch({
      type: 'UPDATE_TOTALS',
      payload: { subtotal, tax, total },
    });
  };

  const validateCart = async (): Promise<boolean> => {
    if (!config?.photographerId) {
      throw new Error('Gallery configuration not loaded');
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const validation = await galleryApiService.validateCart(config.photographerId, state.cart);

      if (!validation.valid) {
        dispatch({ type: 'SET_ERROR', payload: validation.errors.join(', ') });
        return false;
      }

      dispatch({ type: 'SET_LOADING', payload: false });
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Cart validation failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      return false;
    }
  };

  const value: GalleryCartContextType = {
    cart: state.cart,
    loading: state.loading,
    error: state.error,
    addItem,
    removeItem,
    updateQuantity,
    addPackage,
    removePackage,
    applyCoupon,
    removeCoupon,
    clearCart,
    calculateTotals,
    validateCart,
  };

  return <GalleryCartContext.Provider value={value}>{children}</GalleryCartContext.Provider>;
};

export const useGalleryCart = (): GalleryCartContextType => {
  const context = useContext(GalleryCartContext);
  if (context === undefined) {
    throw new Error('useGalleryCart must be used within a GalleryCartProvider');
  }
  return context;
};
